import React, {
  useState,
  useEffect,
} from "react"

import useTitle from "@/hooks/useTitle"

import EtBtnFormBasic
  from "@/components/common/buttons/EtBtnFormBasic"

import PulseLoader
  from "react-spinners/PulseLoader"

// 20 Sep 2026
// Standardized rich text editor
import EtContentEditorLight2
  from "@/utils/EtContentEditorLight2"

import {
  PlusIcon,
  MinusIcon,
} from "@heroicons/react/24/outline"

import {
  useGetOrganizationsQuery,
  useUpdateOrganizationMutation,
} from "./organizationsApiSlice"

import {
  useGetOrganizationTypesQuery,
} from "../organizationTypes/organizationTypesApiSlice"

import {
  useGetUsersQuery,
} from "../users/usersApiSlice"



const EtUpdateOrganization = ({
  id,
  setActiveAdminView,
}) => {

  // First Created - 09 May 2024
  // Author - Atul Pandey
  // Updated - 06 Sep 2024 - added user org role and titles
  // Updated - 01 Mar 2025 - updated the comp with props - { id, setActiveAdminView}
  // Updated - 01 Mar 2025 - switched from navigate to setActiveAdminView
  // Updated - 20 Sep 2026 - standardized rich text editors to EtContentEditorLight2
  // Updated - 20 Sep 2026 - added stable editor keys


  /*
  |--------------------------------------------------------------------------
  | 01 - Page Title
  |--------------------------------------------------------------------------
  */

  useTitle(
    "Update A Organization"
  )


  /*
  |--------------------------------------------------------------------------
  | 02 - Organization
  |--------------------------------------------------------------------------
  */

  const {
    organization,
  } =
    useGetOrganizationsQuery(
      "Organization",
      {
        selectFromResult: ({
          data,
        }) => ({
          organization:
            data
              ?.entities
              ?.[id],
        }),
      }
    )


  /*
  |--------------------------------------------------------------------------
  | 03 - Loading
  |--------------------------------------------------------------------------
  */

  if (
    !organization
  ) {

    return (
      <PulseLoader
        color={"#ea580c"}
      />
    )

  }


  /*
  |--------------------------------------------------------------------------
  | 04 - Form
  |--------------------------------------------------------------------------
  */

  return (

    <EtUpdateOrganizationForm
      organization={
        organization
      }
      id={
        id
      }
      setActiveAdminView={
        setActiveAdminView
      }
    />

  )

}



const EtUpdateOrganizationForm = ({
  organization,
  id,
  setActiveAdminView,
}) => {

  // First Created - 09 May 2024
  // Author - Atul Pandey
  // Updated - 06 Sep 2024 - added user org role and titles
  // Updated - 20 Sep 2026 - EtContentEditorLight2
  // Updated - 20 Sep 2026 - stable editor keys


  /*
  |--------------------------------------------------------------------------
  | 01 - Update Mutation
  |--------------------------------------------------------------------------
  */

  const [
    updateOrganization,
    {
      isLoading,
      isSuccess,
      isError,
      error,
    },
  ] =
    useUpdateOrganizationMutation()


  /*
  |--------------------------------------------------------------------------
  | 02 - Form Data
  |--------------------------------------------------------------------------
  */

  const [
    formData,
    setFormData,
  ] =
    useState({

      organizationTypeId:
        organization
          .organizationTypeId
          ?._id ||
        "",

      organizationName:
        organization
          .organizationName ||
        "",

      organizationDesc:
        organization
          .organizationDesc ||
        "",

      organizationPurpose:
        organization
          .organizationPurpose ||
        "",

      organizationIntro:
        organization
          .organizationIntro ||
        "",

      organizationOutro:
        organization
          .organizationOutro ||
        "",

      organizationTargetAudience:
        organization
          .organizationTargetAudience ||
        [
          {
            audience:
              "",
          },
        ],

      organizationLocations:
        organization
          .organizationLocations ||
        [
          {
            location:
              "",
          },
        ],

      organizationTeamSize:
        organization
          .organizationTeamSize ||
        "",

      organizationRevenueSize:
        organization
          .organizationRevenueSize ||
        "",

      organizationExistenceDuration:
        organization
          .organizationExistenceDuration ||
        "",

      organizationStartedDate:
        organization
          .organizationStartedDate ||
        "",

      organizationRecognitions:
        organization
          .organizationRecognitions ||
        [
          {
            recognition:
              "",
          },
        ],

      organizationContributions:
        organization
          .organizationContributions ||
        [
          {
            contribution:
              "",
          },
        ],

      aboutMe:
        organization
          .aboutMe ||
        "",

      organizationSponsors:
        organization
          .organizationSponsors ||
        [
          {
            sponsorId:
              "",
          },
        ],

    })


  /*
  |--------------------------------------------------------------------------
  | 03 - Stable Editor Keys
  |--------------------------------------------------------------------------
  |
  | These keys are deliberately deterministic.
  |
  | Do not use:
  |
  | - Date.now()
  | - Math.random()
  | - render counters
  | - changing state values
  |
  | The organization id identifies the record and the suffix identifies
  | the specific editor within the form.
  |
  */

  const organizationIntroEditorKey =
    `organization-${id}-intro`

  const organizationOutroEditorKey =
    `organization-${id}-outro`


  /*
  |--------------------------------------------------------------------------
  | 04 - Users / Eligible Sponsors
  |--------------------------------------------------------------------------
  */

  const {
    data:
      usersData,
  } =
    useGetUsersQuery(
      "User"
    )


  const eligibleSponsors =
    usersData
      ?.ids
      ?.map(
        userId =>
          usersData
            .entities
            [userId]
      )
      ?.filter(
        user =>
          (
            user
              ?.userOrgId
              ?._id ===
            organization
              ?._id
          ) &&
          (
            user
              ?.roles
              ?.includes(
                "OrgSponsor"
              )
          )
      ) ||
    []


  /*
  |--------------------------------------------------------------------------
  | 05 - Organization Types
  |--------------------------------------------------------------------------
  */

  const {
    organizationTypes,
  } =
    useGetOrganizationTypesQuery(
      "organizationTypesList",
      {
        selectFromResult: ({
          data,
        }) => ({
          organizationTypes:
            data
              ?.ids
              ?.map(
                organizationTypeId =>
                  data
                    ?.entities
                    ?.[organizationTypeId]
              ),
        }),
      }
    )


  /*
  |--------------------------------------------------------------------------
  | 06 - Organization Type Options
  |--------------------------------------------------------------------------
  */

  const organizationTypeOptions =
    organizationTypes
      ? [
          <option
            key="placeholder"
            value=""
            disabled
          >
            Select an Organization Type
          </option>,

          ...organizationTypes
            .map(
              organizationType => (

                <option
                  key={
                    organizationType
                      .id
                  }
                  value={
                    organizationType
                      .id
                  }
                >
                  {
                    organizationType
                      .organizationTypeName
                  }
                </option>

              )
            ),
        ]
      : []


  /*
  |--------------------------------------------------------------------------
  | 07 - Reset After Successful Update
  |--------------------------------------------------------------------------
  */

  useEffect(
    () => {

      if (
        !isSuccess
      ) {

        return

      }


      setFormData({

        organizationTypeId:
          "",

        organizationName:
          "",

        organizationDesc:
          "",

        organizationPurpose:
          "",

        organizationIntro:
          "",

        organizationOutro:
          "",

        organizationTargetAudience:
          [
            {
              audience:
                "",
            },
          ],

        organizationLocations:
          [
            {
              location:
                "",
            },
          ],

        organizationTeamSize:
          "",

        organizationRevenueSize:
          "",

        organizationExistenceDuration:
          "",

        organizationStartedDate:
          "",

        organizationRecognitions:
          [
            {
              recognition:
                "",
            },
          ],

        organizationContributions:
          [
            {
              contribution:
                "",
            },
          ],

        aboutMe:
          "",

        pic:
          "",

        organizationSponsors:
          [
            {
              sponsorId:
                "",
            },
          ],

      })


      setActiveAdminView(
        "organizations"
      )

    },
    [
      isSuccess,
      setActiveAdminView,
    ]
  )


  /*
  |--------------------------------------------------------------------------
  | 08 - Standard Form Change
  |--------------------------------------------------------------------------
  */

  const handleChange =
    event => {

      const {
        name,
        value,
        files,
      } =
        event.target


      if (
        name ===
        "pic"
      ) {

        setFormData(
          currentFormData => ({
            ...currentFormData,

            pic:
              files
                ?.[0] ||
              "",
          })
        )

        return

      }


      setFormData(
        currentFormData => ({
          ...currentFormData,

          [name]:
            value,
        })
      )

    }


  /*
  |--------------------------------------------------------------------------
  | 09 - Rich Text Editor Changes
  |--------------------------------------------------------------------------
  */

  const handleOrganizationIntroChange =
    content => {

      setFormData(
        currentFormData => ({
          ...currentFormData,

          organizationIntro:
            content ||
            "",
        })
      )

    }


  const handleOrganizationOutroChange =
    content => {

      setFormData(
        currentFormData => ({
          ...currentFormData,

          organizationOutro:
            content ||
            "",
        })
      )

    }


  /*
  |--------------------------------------------------------------------------
  | 10 - Generic Add
  |--------------------------------------------------------------------------
  */

  const handleAdd =
    (
      fieldName,
      newItem
    ) => {

      setFormData(
        currentFormData => ({
          ...currentFormData,

          [fieldName]: [
            ...currentFormData[
              fieldName
            ],

            newItem,
          ],
        })
      )

    }


  /*
  |--------------------------------------------------------------------------
  | 11 - Generic Remove
  |--------------------------------------------------------------------------
  */

  const handleRemove =
    (
      fieldName,
      index
    ) => {

      setFormData(
        currentFormData => ({
          ...currentFormData,

          [fieldName]:
            currentFormData[
              fieldName
            ]
              .filter(
                (
                  _,
                  itemIndex
                ) =>
                  itemIndex !==
                  index
              ),
        })
      )

    }


  /*
  |--------------------------------------------------------------------------
  | 12 - Generic Nested Field Change
  |--------------------------------------------------------------------------
  */

  const handleFieldChange =
    (
      fieldName,
      index,
      value
    ) => {

      setFormData(
        currentFormData => ({

          ...currentFormData,

          [fieldName]:
            currentFormData[
              fieldName
            ]
              .map(
                (
                  item,
                  itemIndex
                ) =>
                  itemIndex ===
                  index
                    ? value
                    : item
              ),

        })
      )

    }


  /*
  |--------------------------------------------------------------------------
  | 13 - Target Audience
  |--------------------------------------------------------------------------
  */

  const handleAddAudience =
    () =>
      handleAdd(
        "organizationTargetAudience",
        {
          audience:
            "",
        }
      )


  const handleRemoveAudience =
    index =>
      handleRemove(
        "organizationTargetAudience",
        index
      )


  const handleAudienceChange =
    (
      index,
      value
    ) =>
      handleFieldChange(
        "organizationTargetAudience",
        index,
        {
          audience:
            value,
        }
      )


  /*
  |--------------------------------------------------------------------------
  | 14 - Locations
  |--------------------------------------------------------------------------
  */

  const handleAddLocations =
    () =>
      handleAdd(
        "organizationLocations",
        {
          location:
            "",
        }
      )


  const handleRemoveLocations =
    index =>
      handleRemove(
        "organizationLocations",
        index
      )


  const handleLocationsChange =
    (
      index,
      value
    ) =>
      handleFieldChange(
        "organizationLocations",
        index,
        {
          location:
            value,
        }
      )


  /*
  |--------------------------------------------------------------------------
  | 15 - Recognitions
  |--------------------------------------------------------------------------
  */

  const handleAddRecognitions =
    () =>
      handleAdd(
        "organizationRecognitions",
        {
          recognition:
            "",
        }
      )


  const handleRemoveRecognitions =
    index =>
      handleRemove(
        "organizationRecognitions",
        index
      )


  const handleRecognitionsChange =
    (
      index,
      value
    ) =>
      handleFieldChange(
        "organizationRecognitions",
        index,
        {
          recognition:
            value,
        }
      )


  /*
  |--------------------------------------------------------------------------
  | 16 - Sponsors
  |--------------------------------------------------------------------------
  */

  const handleAddSponsor =
    () =>
      handleAdd(
        "organizationSponsors",
        {
          sponsorId:
            "",
        }
      )


  const handleRemoveSponsor =
    index =>
      handleRemove(
        "organizationSponsors",
        index
      )


  const handleSponsorChange =
    (
      index,
      value
    ) =>
      handleFieldChange(
        "organizationSponsors",
        index,
        {
          sponsorId:
            value,
        }
      )


  /*
  |--------------------------------------------------------------------------
  | 17 - Submit
  |--------------------------------------------------------------------------
  */

  const handleSubmit =
    async event => {

      event
        .preventDefault()


      const organizationData = {

        organizationTypeId:
          formData
            .organizationTypeId,

        organizationName:
          formData
            .organizationName,

        organizationDesc:
          formData
            .organizationDesc,

        organizationIntro:
          formData
            .organizationIntro,

        organizationOutro:
          formData
            .organizationOutro,

        organizationTargetAudience:
          formData
            .organizationTargetAudience,

        organizationLocations:
          formData
            .organizationLocations,

        organizationRecognitions:
          formData
            .organizationRecognitions,

        organizationStartedDate:
          formData
            .organizationStartedDate,

        aboutMe:
          formData
            .aboutMe,

        organizationSponsors:
          formData
            .organizationSponsors,

      }


      /*
      |--------------------------------------------------------------------------
      | Image Present
      |--------------------------------------------------------------------------
      */

      if (
        formData.pic
      ) {

        const reader =
          new FileReader()


        reader.onloadend =
          async () => {

            organizationData.pic =
              reader.result


            try {

              organizationData.id =
                id


              console.log(
                "Form submitted."
              )


              await updateOrganization(
                organizationData
              )
                .unwrap()

            } catch (
              error
            ) {

              console.error(
                error
              )

            }

          }


        reader.readAsDataURL(
          formData.pic
        )


        return

      }


      /*
      |--------------------------------------------------------------------------
      | No Image
      |--------------------------------------------------------------------------
      */

      try {

        organizationData.id =
          id


        console.log(
          "Form submitted."
        )


        await updateOrganization(
          organizationData
        )
          .unwrap()

      } catch (
        error
      ) {

        console.error(
          error
        )

      }

    }


  /*
  |--------------------------------------------------------------------------
  | 18 - Cancel
  |--------------------------------------------------------------------------
  */

  const handleCancel =
    () => {

      setActiveAdminView(
        "organizations"
      )

    }


  /*
  |--------------------------------------------------------------------------
  | 19 - Clear
  |--------------------------------------------------------------------------
  */

  const handleClear =
    () => {

      setFormData({

        organizationTypeId:
          "",

        organizationName:
          "",

        organizationDesc:
          "",

        organizationPurpose:
          "",

        organizationIntro:
          "",

        organizationOutro:
          "",

        organizationTargetAudience:
          [
            {
              audience:
                "",
            },
          ],

        organizationLocations:
          [
            {
              location:
                "",
            },
          ],

        organizationTeamSize:
          "",

        organizationRevenueSize:
          "",

        organizationExistenceDuration:
          "",

        organizationStartedDate:
          "",

        organizationRecognitions:
          [
            {
              recognition:
                "",
            },
          ],

        organizationContributions:
          [
            {
              contribution:
                "",
            },
          ],

        aboutMe:
          "",

        pic:
          "",

        organizationSponsors:
          [
            {
              sponsorId:
                "",
            },
          ],

      })

    }


  /*
  |--------------------------------------------------------------------------
  | 20 - Render
  |--------------------------------------------------------------------------
  */

  return (

    <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black">

      {isLoading && (

        <PulseLoader
          color={"#ea580c"}
        />

      )}


      {isError && (

        <p className="text-orange-700 font-semi-bold py-2">
          {
            error
              ?.data
              ?.message
          }
        </p>

      )}


      <div className="bg-white p-1 m-1 rounded">

        <p className="p-1 m-1 font-bold font-poppins text-xl">
          Update an Organization
        </p>


        <div className="bg-gray-200 rounded m-1 p-1">

          <form
            onSubmit={
              handleSubmit
            }
            className="m-1 p-1 text-xs font-poppins"
          >

            {/*
            |--------------------------------------------------------------------------
            | Organization Type
            |--------------------------------------------------------------------------
            */}

            <div id="organizationTypeSelector">

              <select
                name="organizationTypeId"
                id="organizationTypeId"
                value={
                  formData
                    .organizationTypeId
                }
                onChange={
                  handleChange
                }
                className="w-full rounded p-2 m-1"
              >
                {
                  organizationTypeOptions
                }
              </select>

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Organization Name
            |--------------------------------------------------------------------------
            */}

            <div id="organizationName">

              <input
                type="text"
                name="organizationName"
                placeholder="Enter the organization name here"
                value={
                  formData
                    .organizationName
                }
                onChange={
                  handleChange
                }
                className="w-full rounded p-2 m-1"
              />

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Organization Description
            |--------------------------------------------------------------------------
            */}

            <div id="organizationDesc">

              <input
                type="text"
                name="organizationDesc"
                placeholder="Enter the organization description here"
                value={
                  formData
                    .organizationDesc
                }
                onChange={
                  handleChange
                }
                className="w-full rounded p-2 m-1"
              />

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Organization Started Date
            |--------------------------------------------------------------------------
            */}

            <div id="organizationStartedDate">

              <input
                type="text"
                name="organizationStartedDate"
                placeholder="Enter the date when the organization started"
                value={
                  formData
                    .organizationStartedDate
                }
                onChange={
                  handleChange
                }
                className="w-full rounded p-2 m-1"
              />

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Organization Intro
            |--------------------------------------------------------------------------
            |
            | Stable Editor Key:
            |
            | organization-{organizationId}-intro
            |
            */}

            <div className="bg-white w-full rounded p-2 m-1 max-h-[300px] overflow-y-auto">

              <EtContentEditorLight2
                editorKey={
                  organizationIntroEditorKey
                }
                value={
                  formData
                    .organizationIntro
                }
                handleChange={
                  handleOrganizationIntroChange
                }
                placeholderText={
                  "Write what people would see when this organization starts!"
                }
              />

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Organization Outro
            |--------------------------------------------------------------------------
            |
            | Stable Editor Key:
            |
            | organization-{organizationId}-outro
            |
            */}

            <div className="bg-white w-full rounded p-2 m-1 max-h-[300px] overflow-y-auto">

              <EtContentEditorLight2
                editorKey={
                  organizationOutroEditorKey
                }
                value={
                  formData
                    .organizationOutro
                }
                handleChange={
                  handleOrganizationOutroChange
                }
                placeholderText={
                  "Write what people would see when this organization ends!"
                }
              />

            </div>


            {/*
            |--------------------------------------------------------------------------
            | About Me
            |--------------------------------------------------------------------------
            */}

            <div id="aboutMe">

              <input
                type="text"
                name="aboutMe"
                placeholder="Add something about the personality of this organization"
                value={
                  formData
                    .aboutMe
                }
                onChange={
                  handleChange
                }
                className="w-full rounded p-2 m-1"
              />

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Picture
            |--------------------------------------------------------------------------
            */}

            <div id="pic">

              <input
                type="file"
                name="pic"
                onChange={
                  handleChange
                }
                className="w-full rounded p-2 m-1"
              />

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Organization Sponsors
            |--------------------------------------------------------------------------
            */}

            <div id="sponsors">

              <div className="m-1 p-1 border border-white rounded">

                <div>

                  <p className="font-bold mx-2 px-2 mt-2">
                    Organization Sponsors
                  </p>

                  <p className="text-[8px] mx-2 px-2 mb-2 italic text-gray-600">
                    Add sponsors for this organization
                  </p>

                </div>


                {
                  formData
                    .organizationSponsors
                    .map(
                      (
                        item,
                        index
                      ) => (

                        <div
                          key={
                            index
                          }
                          className="flex justify-start items-center mx-1"
                        >

                          <select
                            value={
                              item
                                .sponsorId
                            }
                            onChange={
                              event =>
                                handleSponsorChange(
                                  index,
                                  event
                                    .target
                                    .value
                                )
                            }
                            className="w-full rounded p-2 m-1 text-xs"
                          >

                            <option value="">
                              Select a sponsor
                            </option>


                            {
                              eligibleSponsors
                                .map(
                                  sponsor => (

                                    <option
                                      key={
                                        sponsor
                                          ._id
                                      }
                                      value={
                                        sponsor
                                          ._id
                                      }
                                    >
                                      {
                                        sponsor
                                          .name
                                      }
                                      {" ("}
                                      {
                                        sponsor
                                          ?.userOrgRole
                                          ?.orgRoleName
                                      }
                                      {" & "}
                                      {
                                        sponsor
                                          ?.userOrgTitle
                                          ?.orgTitleName
                                      }
                                      {")"}
                                    </option>

                                  )
                                )
                            }

                          </select>


                          <div className="rounded bg-red-500 m-1 p-1">

                            <button
                              type="button"
                              onClick={
                                () =>
                                  handleRemoveSponsor(
                                    index
                                  )
                              }
                            >

                              <MinusIcon className="h-5 w-5 text-white" />

                            </button>

                          </div>

                        </div>

                      )
                    )
                }


                <div className="flex justify-start items-center m-1">

                  <p className="font-bold text-xs m-1 p-1">
                    Add Sponsor
                  </p>

                  <button
                    type="button"
                    onClick={
                      handleAddSponsor
                    }
                  >

                    <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-green-500" />

                  </button>

                </div>

              </div>

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Target Audience
            |--------------------------------------------------------------------------
            */}

            <div id="target-audience">

              <div className="m-1 p-1 border border-white rounded">

                <div>

                  <p className="font-bold mx-2 px-2 mt-2">
                    Target Audience
                  </p>

                  <p className="text-[8px] mx-2 px-2 mb-2 italic text-gray-600">
                    Who this organization is for?
                  </p>

                </div>


                {
                  formData
                    .organizationTargetAudience
                    .map(
                      (
                        item,
                        index
                      ) => (

                        <div
                          key={
                            index
                          }
                          className="flex justify-start items-center mx-1"
                        >

                          <input
                            type="text"
                            value={
                              item
                                .audience
                            }
                            onChange={
                              event =>
                                handleAudienceChange(
                                  index,
                                  event
                                    .target
                                    .value
                                )
                            }
                            placeholder="Enter a target audience"
                            className="w-full rounded p-2 m-1 text-xs"
                          />


                          <div className="rounded bg-red-500 m-1 p-1">

                            <button
                              type="button"
                              onClick={
                                () =>
                                  handleRemoveAudience(
                                    index
                                  )
                              }
                            >

                              <MinusIcon className="h-5 w-5 text-white" />

                            </button>

                          </div>

                        </div>

                      )
                    )
                }


                <div className="flex justify-start items-center m-1">

                  <p className="font-bold text-xs m-1 p-1">
                    Add
                  </p>

                  <button
                    type="button"
                    onClick={
                      handleAddAudience
                    }
                  >

                    <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-green-500" />

                  </button>

                </div>

              </div>

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Organization Locations
            |--------------------------------------------------------------------------
            */}

            <div id="pre-work">

              <div className="m-1 p-1 border border-white rounded">

                <div>

                  <p className="font-bold mx-2 px-2 mt-2">
                    Organization Locations
                  </p>

                  <p className="text-[8px] mx-2 px-2 mb-2 italic text-gray-600">
                    What are the locations for this organization?
                  </p>

                </div>


                {
                  formData
                    .organizationLocations
                    .map(
                      (
                        item,
                        index
                      ) => (

                        <div
                          key={
                            index
                          }
                          className="flex justify-start items-center mx-1"
                        >

                          <input
                            type="text"
                            value={
                              item
                                .location
                            }
                            onChange={
                              event =>
                                handleLocationsChange(
                                  index,
                                  event
                                    .target
                                    .value
                                )
                            }
                            placeholder="Enter a pre work for the organization"
                            className="w-full rounded p-2 m-1 text-xs"
                          />


                          <div className="rounded bg-red-500 m-1 p-1">

                            <button
                              type="button"
                              onClick={
                                () =>
                                  handleRemoveLocations(
                                    index
                                  )
                              }
                            >

                              <MinusIcon className="h-5 w-5 text-white" />

                            </button>

                          </div>

                        </div>

                      )
                    )
                }


                <div className="flex justify-start items-center m-1">

                  <p className="font-bold text-xs m-1 p-1">
                    Add
                  </p>

                  <button
                    type="button"
                    onClick={
                      handleAddLocations
                    }
                  >

                    <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-green-500" />

                  </button>

                </div>

              </div>

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Organization Recognitions
            |--------------------------------------------------------------------------
            */}

            <div id="recognitions">

              <div className="m-1 p-1 border border-white rounded">

                <div>

                  <p className="font-bold mx-2 px-2 mt-2">
                    Organization Recognitions
                  </p>

                  <p className="text-[8px] mx-2 px-2 mb-2 italic text-gray-600">
                    What one can expect to achieve out of the organization?
                  </p>

                </div>


                {
                  formData
                    .organizationRecognitions
                    .map(
                      (
                        item,
                        index
                      ) => (

                        <div
                          key={
                            index
                          }
                          className="flex justify-start items-center mx-1"
                        >

                          <input
                            type="text"
                            value={
                              item
                                .recognition
                            }
                            onChange={
                              event =>
                                handleRecognitionsChange(
                                  index,
                                  event
                                    .target
                                    .value
                                )
                            }
                            placeholder="Enter an recognition for the organization"
                            className="w-full rounded p-2 m-1 text-xs"
                          />


                          <div className="rounded bg-red-500 m-1 p-1">

                            <button
                              type="button"
                              onClick={
                                () =>
                                  handleRemoveRecognitions(
                                    index
                                  )
                              }
                            >

                              <MinusIcon className="h-5 w-5 text-white" />

                            </button>

                          </div>

                        </div>

                      )
                    )
                }


                <div className="flex justify-start items-center m-1">

                  <p className="font-bold text-xs m-1 p-1">
                    Add
                  </p>

                  <button
                    type="button"
                    onClick={
                      handleAddRecognitions
                    }
                  >

                    <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-green-500" />

                  </button>

                </div>

              </div>

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Actions
            |--------------------------------------------------------------------------
            */}

            <div
              id="actions"
              className="bg-white m-1 px-1 py-3 rounded"
            >

              <EtBtnFormBasic
                name={
                  "Update"
                }
                type={
                  "create"
                }
                handleClick={
                  handleSubmit
                }
              />


              <EtBtnFormBasic
                name={
                  "Clear"
                }
                type={
                  "clear"
                }
                handleClick={
                  handleClear
                }
              />


              <EtBtnFormBasic
                name={
                  "Cancel"
                }
                type={
                  "cancel"
                }
                handleClick={
                  handleCancel
                }
              />

            </div>

          </form>

        </div>

      </div>

    </div>

  )

}



export default EtUpdateOrganization